import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import i18n from 'i18next';
import { API_BASE_URL } from '../api';
import "./LoadingScreen.scss";

const AuthContext = createContext();

function LoadingScreen() {
  return (
    <div className="auth-loading-screen">
      <span className="spinner-border text-primary" />
    </div>
  );
}

export function AuthProvider({ children }) {
  const [settings, setSettings] = useState({});

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const loading = authLoading || settingsLoading;
  const isAuthenticated = !!user;
  const navigate = useNavigate();

  const checkAuth = () => {
    return axios.get(`${API_BASE_URL}/api/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        if (res.data?.preferredLanguage) {
          i18n.changeLanguage(res.data.preferredLanguage);
        }
        return loadSettings().then(settingsMap => ({ user: res.data, settings: settingsMap }));
      })
      .catch(() => {
        setUser(null);
        const allowedRoutes = ["/login", "/register", "/", "/forgot-password", "/reset-password", "/terms", "/privacy", "/legal-notice", "/contact", "/delete-account", "/desktop"];
        // /backoffice/* is a fully separate auth realm (PlatformAdmin, see
        // BackofficeAuthProvider), and /help/* is the public help center (see
        // AppRoutes.js) — a missing pharmacy session on either is normal, not a
        // reason to bounce the visitor out to the landing page. Public pages live
        // under a /fr, /en, /ar or /es prefix (see LangLayout) — strip it before
        // matching so e.g. "/fr/login" is still recognized as "/login".
        const rawPathname = window.location.pathname;
        const langMatch = rawPathname.match(/^\/(fr|en|ar|es)(\/.*)?$/);
        const matchedPathname = langMatch ? (langMatch[2] || "/") : rawPathname;
        // Strip a trailing slash before matching — a direct hard load of e.g. "/fr/privacy/"
        // (vs. an in-app <Link to="/fr/privacy">, which never has one) would otherwise miss
        // allowedRoutes' exact "/privacy" entry and bounce the visitor to "/".
        const pathname = matchedPathname.length > 1 && matchedPathname.endsWith("/")
          ? matchedPathname.slice(0, -1)
          : matchedPathname;
        // /medicaments/* is the public product catalog (search + product pages).
        const isExempt = pathname.startsWith("/backoffice") || pathname.startsWith("/help") || pathname.startsWith("/medicaments");
        if (!allowedRoutes.includes(pathname) && !isExempt) {
          navigate("/");
        }
        setSettingsLoading(false);
        return null;
      })
      .finally(() => {
        setAuthLoading(false);
      });
  };

  const loadSettings = () => {
    return axios.get(`${API_BASE_URL}/api/v1/setting/getSettings`, {withCredentials: true})
      .then((res) => {
        const map = {};
        res.data.forEach(setting => {
          map[setting.path] = setting.value;
        });
        setSettings(map);
        return map;
      })
      .catch(err => {
        console.error("Erreur lors du chargement des settings :", err);
        return {};
      })
      .finally(() => {
        setSettingsLoading(false);
      });
  };

  const login = () => checkAuth();

  // `user.roles` is confusingly named — despite the name, /api/auth/me populates it with
  // the flattened list of granular right names (e.g. "PATIENTS_CREATE") the backend's
  // @PreAuthorize checks key off, not the human-readable role names (see user.roleNames
  // for those). Centralized here so every permission-gated UI element checks the exact
  // same thing the backend enforces.
  const hasRight = (right) => !!user?.roles?.includes(right);

  const logout = () => {
    return axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true })
      .then(() => {
        setUser(null);
        navigate("/login");
      });
  };

  useEffect(() => {
    login();

    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        // Skip auth endpoints — they handle 401 themselves. Also skip the backoffice realm
        // entirely: not just its own endpoints (/api/backoffice-auth, /api/v1/backoffice,
        // authenticated separately — see BackofficeAuthProvider), but ALSO any pharmacy-
        // scoped endpoint that happens to 401 while the admin is sitting on a /backoffice
        // page (e.g. Table.js's incidental /api/v1/ui-bookmarks call, which carries no
        // "backoffice" marker of its own). Without the route check, that stray 401 bounced
        // the admin straight out of the backoffice to the pharmacy /login.
        const isBackofficeUrl = error.config?.url?.includes('backoffice');
        const onBackofficeRoute = window.location.pathname.startsWith('/backoffice');
        if (error.response?.status === 401 && !error.config?.url?.includes('/api/auth/') && !isBackofficeUrl && !onBackofficeRoute) {
          setUser(null);
          navigate("/login");
        }
        // The pharmacy's subscription went inactive (own login attempt, or a session that
        // was live when it lapsed) — every API call now 403s with this code, kick the user
        // out to a dedicated screen instead of leaving the app half-broken.
        if (error.response?.status === 403 && error.response?.data?.code === 'SUBSCRIPTION_INACTIVE' && !onBackofficeRoute) {
          setUser(null);
          navigate("/account-suspended", { state: { fromGate: true } });
        }
        // The account was disabled by an admin while a session was still live — every API
        // call now 403s with this code, kick the user out immediately instead of waiting
        // for the next login attempt.
        if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_DISABLED' && !onBackofficeRoute) {
          setUser(null);
          navigate("/login");
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, settings, loading, isAuthenticated, login, logout, reloadSettings: loadSettings, hasRight }}>
      {loading ? <LoadingScreen /> : children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
